import { useSelector } from 'react-redux';

import { selectUserProjectId } from 'core/userContext';
import { SMSContentDefinition } from 'modules/graphql/types';
import { useSMSUserData } from 'modules/templateEditor/hooks/useSMSUserData';
import { AndroidAvatar, AndroidMessenger, AndroidShare, IosAvatar, IosIMessage } from 'ui/icons';
import { colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { displayContent } from '../../common/content';
import { OS, TemplateEditViews } from '../../common/types';
import { getDate, getTimeSimple, shortenLinkInMessage } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useSMSLinkShortening } from '../context/SMSLinkShorteningContext';
import { useSMSPreviewContext } from '../context/SMSPreviewContext';
import { getNotificationMsg } from '../utils';
import {
  avatarAndroid,
  avatarIos,
  avatarIosBadge,
  flexDirectionColumn,
  linkText,
  messengerIcon,
  notificationBubble,
  notificationBubbleAndroidBody,
  notificationBubbleAndroidBodyContent,
  notificationBubbleAndroidBodyContentMsg,
  notificationBubbleAndroidBodyContentMsgMedia,
  notificationBubbleAndroidBodyContentMsgTxt,
  notificationBubbleAndroidBodyContentSender,
  notificationBubbleAndroidHeader,
  notificationBubbleAndroidHeaderTime,
  notificationBubbleAvatar,
  notificationBubbleIosContent,
  notificationBubbleIosContentBody,
  notificationBubbleIosContentBodyMedia,
  notificationBubbleIosContentBodyMsg,
  notificationBubbleIosContentHeader,
  notificationBubbleIosContentHeaderSender,
  notificationBubbleIosContentHeaderTime,
  notificationContactCardAndroidAvatarImage,
  notificationContactCardIosAvatarImage,
  notificationContactCardIosContentBodyMsg,
  notificationDate,
  notificationTimeCommon,
  notificationTimeOS,
} from './styles';

type Props = {
  selectedOs: OS;
};

const msgSender = '01234';

export const Notification = ({ selectedOs }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { locale, view } = useQueryParamContext();
  const { messageContent, previewContactCard, fullSmsMessage } = useSMSPreviewContext();
  const { isDataFeedDataEmpty, isUserDataEmpty, userDataIsValid, userData } =
    usePreviewWithDataContext();
  const { userIsUSOrCanada } = useSMSUserData(userDataIsValid, userData);
  const { isSMSLinkShorteningEnabled, shortlinkLength, defaultSmsLinkShorteningDomain } =
    useSMSLinkShortening();
  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const { trackingDomain } = smsData;
  // note: this date and time appear in a localized preview, so we specify the locale of the template
  const dateTime = new Date();
  const time = getTimeSimple(dateTime, locale);
  const date = getDate(dateTime, locale);
  const message = usePopulatedField ? messageContent : fullSmsMessage;
  const projectId = useSelector(selectUserProjectId);
  const truncatedMessageMaybeWithShortenLink = getNotificationMsg(
    selectedOs,
    shortenLinkInMessage(
      message,
      trackingDomain ?? defaultSmsLinkShorteningDomain ?? '',
      projectId,
      shortlinkLength,
      isSMSLinkShorteningEnabled,
    ),
  );

  const renderMedia = (platform: OS) => {
    if (smsData.imageUrl || previewContactCard) {
      if (userIsUSOrCanada) {
        return (
          // contact card render within US/Canada handled in main render body so skip render here
          smsData.imageUrl && (
            <div
              data-test={platform === OS.android && 'android-notification-imageUrl'}
              style={{
                ...(platform === OS.ios
                  ? notificationBubbleIosContentBodyMedia
                  : notificationBubbleAndroidBodyContentMsgMedia),
                backgroundImage: `url(${smsData.imageUrl})`,
              }}
            />
          )
        );
      } else {
        return (
          <>
            <br />
            <div
              style={{
                ...(platform === OS.ios
                  ? notificationBubbleIosContentBodyMsg
                  : notificationBubbleAndroidBodyContentMsgTxt),
                ...linkText,
              }}
            >
              {`https://${trackingDomain || 'itbl.co'}/p9sB7MiV`}
            </div>
          </>
        );
      }
    }
    return null;
  };

  return (
    <>
      <div
        style={{
          ...notificationTimeCommon,
          ...notificationTimeOS[selectedOs],
        }}
      >
        {time}
      </div>
      <div style={notificationDate}>{date}</div>
      {selectedOs === OS.ios ? (
        <div style={notificationBubble[selectedOs]}>
          <div style={notificationBubbleAvatar[selectedOs]}>
            <div style={avatarIos}>
              <IosAvatar height={pxToRem(28)} width={pxToRem(28)} color={colors.white} />
              <IosIMessage height={pxToRem(11.5)} width={pxToRem(11.5)} style={avatarIosBadge} />
            </div>
          </div>
          <div style={notificationBubbleIosContent}>
            <div style={notificationBubbleIosContentHeader}>
              <div style={notificationBubbleIosContentHeaderSender}>{msgSender}</div>
              <div style={notificationBubbleIosContentHeaderTime}>{displayContent.strNow}</div>
            </div>
            <div
              style={{
                ...notificationBubbleIosContentBody,
                ...(!userIsUSOrCanada ? flexDirectionColumn : {}),
              }}
            >
              {/* SMS, Push, & WebPush do not support HTML, so do not use
               * dangerouslySetInnerHTML attr to display content (force text rendering)
               * Email & InApp DO support HTML and should be displayed with
               * dangerouslySetInnerHTML={{ __html: removeScript(content) }} so that HTML
               * renders and scripts are removed prior to rendering
               */}
              {previewContactCard && userIsUSOrCanada ? (
                <>
                  <div
                    data-test="ios-notification-displayName"
                    style={notificationContactCardIosContentBodyMsg}
                  >
                    {previewContactCard.displayName}
                  </div>
                  <img
                    data-test="ios-notification-imageUrl"
                    style={notificationContactCardIosAvatarImage}
                    src={previewContactCard?.imageUrl}
                  />
                </>
              ) : (
                <>
                  <div style={notificationBubbleIosContentBodyMsg}>
                    {truncatedMessageMaybeWithShortenLink}
                  </div>
                  {renderMedia(OS.ios)}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={notificationBubble[selectedOs]}>
          <div style={notificationBubbleAndroidHeader}>
            <div style={notificationBubbleAndroidHeaderTime}>
              <AndroidMessenger height={pxToRem(15)} width={pxToRem(12)} style={messengerIcon} />
              <span>
                {displayContent.strMessages} &bull; {displayContent.strNow}
              </span>
            </div>
            <AndroidShare height={pxToRem(12)} width={pxToRem(15)} />
          </div>
          <div style={notificationBubbleAndroidBody}>
            <div style={notificationBubbleAvatar[selectedOs]}>
              <div style={avatarAndroid}>
                <AndroidAvatar height={pxToRem(24)} width={pxToRem(24)} color={colors.white} />
              </div>
            </div>
            <div style={notificationBubbleAndroidBodyContent}>
              <div style={notificationBubbleAndroidBodyContentSender}>{msgSender}</div>
              <div
                style={{
                  ...notificationBubbleAndroidBodyContentMsg,
                  ...(!userIsUSOrCanada ? flexDirectionColumn : {}),
                }}
              >
                {/* SMS, Push, & WebPush do not support HTML, so do not use
                 * dangerouslySetInnerHTML attr to display content (force text rendering)
                 * Email & InApp DO support HTML and should be displayed with
                 * dangerouslySetInnerHTML={{ __html: removeScript(content) }} so that HTML
                 * renders and scripts are removed prior to rendering
                 */}
                {previewContactCard && userIsUSOrCanada ? (
                  <>
                    <div
                      data-test="android-notification-displayName"
                      style={notificationBubbleAndroidBodyContentMsgTxt}
                    >
                      {previewContactCard.displayName}
                    </div>
                    <img
                      style={notificationContactCardAndroidAvatarImage}
                      src={previewContactCard?.imageUrl}
                    />
                  </>
                ) : (
                  <>
                    <div style={notificationBubbleAndroidBodyContentMsgTxt}>
                      {truncatedMessageMaybeWithShortenLink}
                    </div>
                    {renderMedia(OS.android)}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;
